import {
  resolveWorkflowVariable,
  variableSourceKeyOf,
  type WorkflowVariable,
} from '@bmo/form-core';

import { isAdvisorWritable } from './bmoStatus';

/** The field properties that decide whether and how a slot is filled from a variable. */
export type VariablePrefilledField = {
  type?: string | null;
  sourceValue?: string | null;
  /** SingleSelect only — the authored choices a variable value has to match. */
  options?: readonly { value: string }[] | null;
};

/**
 * What the variable-prefill pass should do with one slot. Deliberately the same shape as
 * {@link import('./advisorPrefill').AdvisorPrefillAction} so `FormShell` writes both passes'
 * results through one branch.
 */
export type VariablePrefillAction = { kind: 'set'; value: string } | { kind: 'leave' };

const LEAVE: VariablePrefillAction = { kind: 'leave' };

/** One slot's current state, as the prefill pass finds it. */
export type VariablePrefillSlot = {
  current: unknown;
  /**
   * The advisor has typed into (or picked in) this exact slot during THIS mount —
   * FormShell's `editedFieldsRef`. Only meaningful for editable prefills; a read-only field
   * renders disabled and emits no change.
   */
  edited?: boolean;
};

/** Where the application stands when the prefill pass runs. */
export type VariablePrefillContext = {
  mode: 'preview' | 'advisor';
  /** The application's `bmoStatus` as the shell holds it — always a real value. */
  status: string;
};

/**
 * Decide what a `variable.<key>`-bound slot should hold.
 *
 * ## Why the workflow's value outranks a restored one
 *
 * A variable is a statement about the WORKFLOW, not about the application: "forms filed through
 * this workflow use this fund". When an originator changes it, the new value is the current
 * truth, and an application resumed from a draft saved under the old value should pick it up —
 * the same argument {@link decideAdvisorPrefill} makes for the advisor's profile, and for the
 * same reason: the snapshot records no provenance, so on a resume nothing distinguishes a value
 * the prefill wrote from one that was typed, and the live source is the better guess.
 *
 * ## What is NOT overwritten
 *
 *  - **Their typing, this session.** Once the advisor touches the slot it is theirs; `edited`
 *    is that knowledge, and a later status change re-running this pass must not take it back.
 *  - **A filed application.** Past {@link isAdvisorWritable} the screen is the record of what
 *    went to BMO, not a form. Rewriting it would show today's configuration over the values the
 *    submission actually carried. Same gate, same vocabulary as the advisor pass — not a second
 *    list to keep in step.
 *  - **A missing or deleted variable.** Resolving to null is the "nothing to say" answer and
 *    leaves the slot untouched. A binding may legitimately outlive its variable (the two are
 *    authored through different endpoints), and blanking the field would turn a stale binding
 *    into data loss.
 *  - **An off-list choice.** See below.
 *
 * `mode='preview'` fills empty slots only, like the advisor pass: preview seeds its values from
 * the originator's own sample fixture, and rewriting there would replace the sample with
 * configuration the fixture was not written against.
 *
 * ## SingleSelect
 *
 * A variable whose value matches no authored option is LEFT, never written. An off-list value
 * would put the dropdown in a state the advisor cannot reproduce and — on a `FundCodeSelector` —
 * would send BMO a fund code this workflow never offered. An empty required dropdown is the
 * honest failure: the advisor is asked the question.
 *
 * Unlike the advisor pass this never CLEARS a slot. Clearing exists there to withdraw a rep code
 * the advisor no longer holds, which is a fact about them going stale; a variable going missing
 * says nothing about the answer already recorded.
 */
export function decideVariablePrefill(
  field: VariablePrefilledField,
  variables: readonly WorkflowVariable[] | null | undefined,
  slot: VariablePrefillSlot,
  context: VariablePrefillContext,
): VariablePrefillAction {
  const key = variableSourceKeyOf(field.sourceValue);
  if (!key) return LEAVE;
  // Filed: BMO owns the application and the screen is a record, not a form.
  if (!isAdvisorWritable(context.status)) return LEAVE;

  const { current } = slot;
  const isEmpty = current === undefined || current === null || current === '';
  if (context.mode === 'preview' && !isEmpty) return LEAVE;

  const resolved = resolveWorkflowVariable(variables, key);
  if (resolved === null) return LEAVE;
  // Their correction, made in front of us.
  if (slot.edited === true) return LEAVE;
  if (resolved === current) return LEAVE;
  // A choice field can only hold one of its own options.
  if (field.type === 'SingleSelect' && !(field.options ?? []).some((o) => o.value === resolved))
    return LEAVE;
  return { kind: 'set', value: resolved };
}

/**
 * Does this field draw its value from a workflow variable?
 *
 * Note what this is NOT: `isAdvisorSourcedField` in form-core, which is the predicate that
 * WITHHOLDS session-sourced fields from the public customer runtime. A variable needs no
 * session, so a variable-bound field must render for an anonymous customer exactly as it does
 * for an advisor. That predicate tests the `advisor.` prefix, so `variable.*` already falls out
 * of it as false — this function is its sibling, never its extension.
 */
export function isVariableSourcedField(field: VariablePrefilledField): boolean {
  return variableSourceKeyOf(field.sourceValue) !== null;
}
